import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { bankingInfoService } from '../../../../services/bankingInfo';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('BankingInfoLockUnlock');

type RouteParams = {
  BankingInfoLockUnlock: {
    bankingId: string;
  };
};

export const BankingInfoLockUnlockScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<RouteParams, 'BankingInfoLockUnlock'>>();
  const bankingId = route.params?.bankingId;
  const [submitting, setSubmitting] = useState(false);

  const handleLockPress = async () => {
    try {
      if (!bankingId) {
        Alert.alert('Error', 'Missing banking ID');
        return;
      }
      setSubmitting(true);
      logger.debug('[Lock Debug] Starting lock process for bankingId:', bankingId);
      
      await bankingInfoService.lockBankingInfo(bankingId);

      // CRITICAL: Mark task as completed
      await tasksService.completeTask('banking');

      logger.debug('[Lock Debug] Lock API call completed successfully');
      navigation.navigate('BankingInfoLocked');
    } catch (e) {
      logger.error('[Lock Debug] Error during lock:', e);
      Alert.alert('Error', 'Failed to lock banking information');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlockPress = async () => {
    if (!bankingId) {
      Alert.alert('Error', 'Missing banking ID');
      return;
    }

    try {
      setSubmitting(true);
      await tasksService.completeTask('banking');
      (navigation as any).navigate('BankingInfoUnlocked', { bankingId });
    } catch (error) {
      logger.error('Error completing task:', error);
      (navigation as any).navigate('BankingInfoUnlocked', { bankingId });
    } finally {
      setSubmitting(false);
    }
  };

    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
        </View>

        <View style={styles.content}>
          <Text style={styles.title}>Banking Information Completed</Text>


          <View style={styles.blueContainer}>
            <Text style={styles.messageText}>
              You're all set! Your Banking information has been successfully entered! Would like to share this information with your successor or Key Holders?
            </Text>
          </View>

          <View style={styles.grayButtonContainer}>
            {submitting ? (
              <ActivityIndicator size="large" color="#1C2C4F" />
            ) : null}
            <TouchableOpacity
              style={[styles.button, styles.lockButton]}
              onPress={handleLockPress}
            >
              <Image
                source={require('../../../../assets/icons/BlueLock.png')}
                style={styles.buttonIcon}
              />
              <Text style={[styles.buttonText, styles.lockText]}>Keep it locked!</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.button, styles.unlockButton]}
              onPress={handleUnlockPress}
            >
              <Image
                source={require('../../../../assets/icons/BlueUnlock.png')}
                style={styles.buttonIcon}
              />
              <Text style={[styles.buttonText, styles.unlockText]}>I Want to Unlock it!</Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: '#F3F6F8',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      padding: ms(15),
      paddingTop: ms(15),
      position: 'relative',
    },
    backButton: {
      position: 'absolute',
      left: ms(50),
      top: '25%',
      transform: [{ translateY: -12 }],
      padding: ms(5),
      zIndex: 1,
    },
    backIcon: {
      width: ms(32, 0.3),
      height: ms(32, 0.3),
      tintColor: '#333',
    },
    logo: {
      width: ms(80, 0.3),
      height: ms(80, 0.3),
    },
    profileContainer: {
      position: 'absolute',
      right: ms(15),
      top: '50%',
      transform: [{ translateY: -12 }],
      width: ms(45, 0.3),
      height: ms(45, 0.3),
      borderRadius: ms(22.5),
      backgroundColor: '#fff',
      padding: ms(2),
    },
    profileImage: {
      width: '100%',
      height: '100%',
      borderRadius: ms(21),
    },
    content: {
      flex: 1,
      justifyContent: 'space-between',
    },
    title: {
      fontSize: ms(18, 0.3),
      fontWeight: 'bold',
      color: '#1C2C4F',
      textAlign: 'center',
      marginTop: ms(20),
      paddingHorizontal: ms(20),
    },
    subtitle: {
      fontSize: ms(24, 0.3),
      color: '#1C2C4F',
      textAlign: 'center',
      marginTop: ms(5),
    },
    blueContainer: {
      backgroundColor: '#1C2C4F',
      padding: ms(20),
      paddingBottom: ms(180),
      borderTopLeftRadius: ms(30),
      borderTopRightRadius: ms(30),
      position: 'absolute',
      bottom: ms(120),
      left: 0,
      right: 0,
      zIndex: 1,
    },
    messageText: {
      color: '#FFFFFF',
      fontSize: ms(18, 0.3),
      lineHeight: ms(30, 0.3),
      textAlign: 'center',
      paddingHorizontal: ms(25),
    },
    grayButtonContainer: {
      backgroundColor: '#F3F6F8',
      padding: ms(20),
      paddingTop: ms(30),
      paddingBottom: ms(60),
      borderRadius: ms(30),
      gap: ms(25),
      position: 'absolute',
      bottom: ms(20),
      left: 0,
      right: 0,
      zIndex: 2,
    },
    button: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: ms(10),
      paddingLeft: ms(25),
      borderRadius: ms(50),
      gap: ms(50),
    },
    buttonIcon: {
      width: ms(50, 0.3),
      height: ms(50, 0.3),
      resizeMode: 'contain',
    },
    buttonText: {
      fontSize: ms(18, 0.3),
      fontWeight: '600',
      alignSelf: 'center',
    },
    lockButton: {
      backgroundColor: '#FF9F5A',
    },
    unlockButton: {
      backgroundColor: '#29BFC0',
    },
    lockText: {
      color: '#FFFFFF',
    },
    unlockText: {
      color: '#FFFFFF',
    },
  });